import React, { useEffect, useState, Suspense } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import Login from './components/Login';
import { BootScreen, RouteProgress } from './components/Loader'; // LOADER REDESIGN 2026-05-26
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { setAuth0TokenGetter, setAuth0Logout } from './services/authService';
import './App.css';
import './styles/mobile.css';

// Code-split the heavy authenticated app and the print-only layout off the
// initial bundle. Login stays eager — it's tiny and on the unauthenticated path.
const MainDashboard = React.lazy(() => import('./components/MainDashboard'));
const PrintLayout = React.lazy(() => import('./components/PrintLayout'));

function App() {
  const location = useLocation();

  // /print/* routes bypass Auth0 entirely — they use a server-signed JWT
  if (location.pathname.startsWith('/print/')) {
    return (
      <ThemeProvider>
        <LanguageProvider>
          <BrandingProvider>
            <Suspense fallback={<BootScreen />}>
              <PrintLayout />
            </Suspense>
          </BrandingProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0();
  const [authVerified, setAuthVerified] = useState(false);
  const [authCheckLoading, setAuthCheckLoading] = useState(true);

  // Set the token getter and logout functions for authService
  useEffect(() => {
    setAuth0TokenGetter(getAccessTokenSilently);
    setAuth0Logout(logout);
  }, [getAccessTokenSilently, logout]);

  // Verify authentication is actually working by checking if we can get a token
  useEffect(() => {
    const verifyAuthentication = async () => {
      if (!isAuthenticated) {
        setAuthCheckLoading(false);
        setAuthVerified(false);
        return;
      }

      try {
        // Try to get a token - if this fails, user is not actually authenticated
        const token = await getAccessTokenSilently();
        if (token) {
          setAuthVerified(true);
        } else {
          // No token available, authentication failed
          setAuthVerified(false);
        }
      } catch (error) {
        console.error('Authentication verification failed:', error);
        // Token retrieval failed, user needs to re-authenticate
        setAuthVerified(false);
        // Logout to clear invalid auth state
        logout({ logoutParams: { returnTo: window.location.origin } });
      } finally {
        setAuthCheckLoading(false);
      }
    };

    if (!isLoading) {
      verifyAuthentication();
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently, logout]);

  // Periodically verify authentication is still valid while user is using the app
  // This catches cases where token expires while user is actively using the app
  useEffect(() => {
    if (!isAuthenticated || !authVerified) {
      return;
    }

    const checkAuthInterval = setInterval(async () => {
      try {
        // Try to get a token - if this fails, authentication has expired
        const token = await getAccessTokenSilently();
        if (!token) {
          console.warn('Authentication token expired during session');
          setAuthVerified(false);
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      } catch (error) {
        console.error('Periodic authentication check failed:', error);
        setAuthVerified(false);
        logout({ logoutParams: { returnTo: window.location.origin } });
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(checkAuthInterval);
  }, [isAuthenticated, authVerified, getAccessTokenSilently, logout]);

  const handleLoginSuccess = () => {
    // This is handled by Auth0 automatically
  };

  const handleLogout = () => {
    // This will be handled by MainDashboard using Auth0
  };

  if (isLoading || authCheckLoading) {
    return (
      <ThemeProvider>
        <LanguageProvider>
          <BrandingProvider>
            <BootScreen />
          </BrandingProvider>
        </LanguageProvider>
      </ThemeProvider>
    );
  }

  // Only show MainDashboard if user is authenticated AND verified
  // If isAuthenticated is true but authVerified is false, show login
  const shouldShowDashboard = isAuthenticated && authVerified;

  return (
    <ThemeProvider>
      <LanguageProvider>
        <BrandingProvider>
          <div className="App">
            {/* LOADER REDESIGN 2026-05-26 — top route-progress bar. Remove this line to roll back. */}
            <RouteProgress />
            {shouldShowDashboard ? (
              <Suspense fallback={<BootScreen />}>
                <MainDashboard onLogout={handleLogout} />
              </Suspense>
            ) : (
              <Login onLoginSuccess={handleLoginSuccess} />
            )}
          </div>
        </BrandingProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
